import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    document.addEventListener('abris:cart-synced', this.onCartSynced);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
    document.removeEventListener('abris:cart-synced', this.onCartSynced);
    this.cartRequest?.abort();
    this.cartRevision = (this.cartRevision || 0) + 1;
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartLinesUpdateEvent} event - The cart update event.
   */
  onCartUpdate = (event) => {
    const revision = this.cartRevision = (this.cartRevision || 0) + 1;
    this.cartRequest?.abort();
    const refresh = () => {
      if (this.isConnected && revision === this.cartRevision) this.ensureCartBubbleIsCorrect();
    };
    if (event.promise) event.promise.then(refresh, refresh);
    else refresh();
  };

  onCartSynced = (event) => {
    const count = event.detail?.itemCount;
    if (!Number.isInteger(count) || count < 0) return;
    this.cartRevision = (this.cartRevision || 0) + 1;
    this.cartRequest?.abort();
    this.renderCartBubble(count, false);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The absolute number of items in the cart.
   * @param {boolean} [animate=true] - Whether to animate the bubble.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = async () => {
    if (!this.refs.cartBubbleCount) return;
    const revision = this.cartRevision = (this.cartRevision || 0) + 1;
    this.cartRequest?.abort();
    this.cartRequest = new AbortController();
    try {
      const response = await fetch(Theme.routes.cart_url.replace(/\/$/, '') + '.js', {
        headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store',
        signal: this.cartRequest.signal
      });
      if (!response.ok) throw new Error('Cart count refresh failed: ' + response.status);
      const cart = await response.json();
      if (this.isConnected && revision === this.cartRevision && Number.isInteger(cart.item_count) && cart.item_count >= 0) {
        this.renderCartBubble(cart.item_count, false);
      }
    } catch (error) {
      if (error.name !== 'AbortError') console.warn('[cart-icon] Could not synchronize cart count:', error);
    }
  };}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}

